import { PaperAirplaneIcon, XIcon } from '@heroicons/react/outline';
import { PaperAirplaneIcon as PaperAirplaneIconSolid } from '@heroicons/react/solid';
import { Avatar, Drawer, Input, Skeleton } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import StarWithoutBg from 'assets/Icons/StarWithoutBg';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import moment from 'moment';
import Image from 'next/image';
import { useRouter } from 'next/router';
import React, { KeyboardEvent, useEffect, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';

import ScrollableFeed from './ScrollableFeed';
import { AiChatMessage } from '../../ChatBox/AiChatMessage';
import { Message } from '../../ChatBox/Message';
import ParserV2 from '../../shared/basic/Parser/ParserV2';
import { ShouldRender } from '../../shared/basic/ShouldRender';
import { formateErrorObject } from '../../shared/formateErrorObject';
import { notify } from '../../shared/notify';
import Logo from '../../../../assets/Icons/Logo';
import { user } from '../../../atoms/user';
import { EventDetails, logEvent } from '../../../hooks/useClickEventTracking';
import { Endpoints } from '../../../network';
import { askQuestion } from '../../../services/AiChat';
import { User } from '../../../utils/interfaces/User';

interface ModalComponentProps {
  visible: boolean;
  onCancel: () => void;
  isClosable?: boolean;
  bookData?: {
    name: string;
    image: string;
  };
  isFullScreen?: boolean;
  isGlobal?: boolean;
}

interface Message {
  sender: { id: number; name?: string };
  messages: string;
  session_id?: string;
  createdAt?: string;
}

const AiChatDrawer: React.FC<ModalComponentProps> = ({
  visible,
  onCancel,
  bookData,
  isFullScreen,
  isGlobal = false,
}) => {
  const [height, setHeight] = useState<number>(0);
  const [scrolled, setScrolled] = useState<boolean>(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [prompt, setPrompt] = useState('');
  const [chadProcessing, setChadProcessing] = useState(false);
  const userData: User | undefined = useRecoilValue(user);
  const router = useRouter();
  const baseUrl = process.env.NEXT_PUBLIC_API_READER_URL;
  const { book_id, bookId } = router.query;
  const lastMessageRef = useRef<HTMLDivElement>(null);
  const chatBoxRef = useRef<HTMLDivElement>(null);

  const parsedBookId = bookId ? (bookId as string).split('-').pop() || '' : '';

  const containerRef = useRef<HTMLDivElement>(null);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const screens = useBreakpoint();

  useEffect(() => {
    const handleResize = () => {
      if (typeof window !== 'undefined') {
        const adjustedHeight = window.innerHeight - (isGlobal ? 20 : 158);
        setHeight(adjustedHeight);
      }
    };

    const handleScroll = () => {
      if (typeof window !== 'undefined') {
        const scrolledAmount = window.scrollY;
        const adjustedHeight = window.innerHeight - (isGlobal ? 20 : 140);
        setHeight(adjustedHeight);
        setScrolled(scrolledAmount > 0);
      }
    };

    handleResize();

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize);
      window.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('scroll', handleScroll);
      }
    };
  }, [isGlobal]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        chatBoxRef.current &&
        !chatBoxRef.current.contains(event.target as Node)
      ) {
        onCancel();
      }
    }

    if (visible) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [visible]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.5,
      },
    },
  };

  const wordVariants = {
    hidden: { opacity: 0, x: -10 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.5 },
    },
  };

  const skeletonLines = [
    ['w-20 md:w-32', 'hidden md:w-24', 'w-full'],
    ['w-full', 'w-full', 'w-24'],
    ['w-full', 'w-20', 'w-full'],
    ['w-full', 'w-full', 'w-24'],
    ['md:w-32', 'w-24', 'hidden md:w-full'],
  ];

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAskQuestion();
    }
  };

  const handleAskQuestion = async () => {
    try {
      const lastSessionId =
        messages.length > 0 ? messages[messages.length - 1]?.session_id : '';

      setMessages((prevMessages) => [
        ...prevMessages,
        {
          sender: { id: 1 },
          messages: prompt,
          session_id: lastSessionId,
          createdAt: moment()?.toLocaleString(),
        },
      ]);
      const eventParams: EventDetails = {
        event_name: 'ai_query',
        source: 'ask_ai',
        source_type: 'book',
        source_id: (book_id ?? parsedBookId) as string,
        sub_source: null,
        sub_source_id: null,
        unit: 'boolean',
        value: 1,
        meta: {
          click_source: 'ask_ai_screen',
          ai_query: prompt,
        },
      };

      logEvent({
        eventDetails: eventParams,
        userData: userData,
        pathname: router.pathname,
      });
      setPrompt('');
      setChadProcessing(true);
      const data = await askQuestion({
        book_id: (book_id ?? parsedBookId) as string,
        question: prompt,
        ...(messages.length ? { session_id: lastSessionId } : {}),
      });

      setMessages((prevMessages) => [
        ...prevMessages,
        {
          sender: { id: 2, name: 'A' },
          messages: data.result,
          session_id: data.session_id || lastSessionId,
          createdAt: moment
            .utc(data?.created_at, 'ddd, DD MMM YYYY HH:mm:ss [GMT]')
            .local()
            .format('YYYY-MM-DD HH:mm:ss'),
        },
      ]);
    } catch (error: any) {
      const message = formateErrorObject(error);
      notify(message, 'error');
    } finally {
      setChadProcessing(false);
    }
  };
  useEffect(() => {
    if (visible && !screens.lg) {
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      document.body.style.position = '';
      document.body.style.width = '';
    }

    return () => {
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, [visible, screens]);

  useEffect(() => {
    if (visible && textareaRef.current && !chadProcessing) {
      textareaRef.current.focus();
    }
  }, [visible, chadProcessing]);

  const [isKeyboardOpen, setIsKeyboardOpen] = useState<boolean>(false);
  const inputRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (lastMessageRef.current) {
      lastMessageRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [messages]);

  useEffect(() => {
    const handleResize = () => {
      if (
        window.visualViewport &&
        window.visualViewport.height < window.innerHeight
      ) {
        setIsKeyboardOpen(true);
      } else {
        setIsKeyboardOpen(false);
      }
    };

    window.visualViewport?.addEventListener('resize', handleResize);
    window.addEventListener('resize', handleResize);

    return () => {
      window.visualViewport?.removeEventListener('resize', handleResize);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (isKeyboardOpen && inputRef.current) {
      inputRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [isKeyboardOpen]);

  const renderContent = (
    <div
      style={{
        transition: 'transform 0.3s ease, opacity 0.3s ease',
      }}
      className={`w-full  h-full lg:h-auto lg:p-3 font-lato font-semibold no-scrollbar text-white inline-flex items-center cursor-pointer  `}
    >
      <div
        style={{
          height: screens.md ? `${height}px` : '100%',
          transition: 'height 0.3s ease-in-out',
        }}
        className={clsx(
          screens.lg ? `lg:h-[${height}]px` : 'h-full',
          `overflow-hidden`,
          'w-full ',
        )}
      >
        <div className='flex-1 h-full no-scrollbar'>
          <div className='shadow-md w-full overflow-y-auto lg:rounded-b-[20px] h-full flex  flex-col'>
            <span
              className={clsx(
                isGlobal ? 'bg-grayZl-950' : 'bg-[#1E1E1F]',
                'sticky w-full z-20 top-0  lg:rounded-t-[20px] px-8 py-3 border-cartBgColor border-b-2 ',
              )}
            >
              <span className='flex justify-between'>
                <ShouldRender check={bookData}>
                  <div className='flex items-centers gap-x-[14px] h-full'>
                    <div className=' translate-y-1'>
                      <Image
                        width={24}
                        height={40}
                        alt='BookImage'
                        src={`${baseUrl}/${Endpoints.getCoverImagesForBooks}/${(book_id ?? parsedBookId) as string}`}
                        className='rounded-sm'
                      />
                    </div>
                    <div className='flex  justify-start flex-col  h-full'>
                      <div className='text-start flex justify-start'>
                        Ask AI
                      </div>

                      <div className='text-start text-xs md:text-sm text-textDarkGrayColor flex justify-start'>
                        <ParserV2
                          isPopOver={false}
                          text={bookData?.name || ''}
                          maxWords={!screens.xl ? 4 : 8}
                        />
                      </div>
                    </div>
                  </div>
                </ShouldRender>
                <span className='flex items-center'>
                  <span
                    onClick={() => onCancel()}
                    className=' rounded-full p-1.5 cursor-pointer border border-[#2D2D2D]  flex justify-center items-center'
                  >
                    <XIcon className='w-4 h-4  text-[#787878] cursor-pointer' />
                  </span>
                </span>
                <ShouldRender check={isGlobal}>
                  <div className='flex items-center justify-center flex-col'>
                    <span className='font-semibold text-sm flex items-center justify-center gap-x-1'>
                      {' '}
                      <svg
                        width='16'
                        height='16'
                        viewBox='0 0 16 16'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'
                      >
                        <g clip-path='url(#clip0_17829_51195)'>
                          <path
                            d='M6.15346 4.9902C6.91392 2.93509 7.29415 1.90754 7.96219 1.88226C7.98739 1.8813 8.01261 1.8813 8.03781 1.88226C8.70585 1.90754 9.08608 2.93509 9.84654 4.9902C9.99864 5.40123 10.0747 5.60675 10.2225 5.75856C10.2287 5.76497 10.235 5.77128 10.2414 5.77752C10.3933 5.92532 10.5988 6.00136 11.0098 6.15346C13.0649 6.91392 14.0925 7.29415 14.1177 7.96219C14.1187 7.98739 14.1187 8.01261 14.1177 8.03782C14.0925 8.70585 13.0649 9.08608 11.0098 9.84654C10.5988 9.99864 10.3933 10.0747 10.2414 10.2225C10.235 10.2287 10.2287 10.235 10.2225 10.2414C10.0747 10.3933 9.99864 10.5988 9.84654 11.0098C9.08608 13.0649 8.70585 14.0925 8.03782 14.1177C8.01261 14.1187 7.98739 14.1187 7.96219 14.1177C7.29415 14.0925 6.91392 13.0649 6.15346 11.0098C6.00136 10.5988 5.92532 10.3933 5.77752 10.2414C5.77128 10.235 5.76497 10.2287 5.75856 10.2225C5.60675 10.0747 5.40123 9.99864 4.9902 9.84654C2.93509 9.08608 1.90754 8.70585 1.88226 8.03781C1.8813 8.01261 1.8813 7.98739 1.88226 7.96219C1.90754 7.29415 2.93509 6.91392 4.9902 6.15346C5.40123 6.00136 5.60675 5.92532 5.75856 5.77752C5.76497 5.77128 5.77128 5.76497 5.77752 5.75856C5.92532 5.60675 6.00136 5.40123 6.15346 4.9902Z'
                            fill='url(#paint0_linear_17829_51195)'
                          />
                        </g>
                        <defs>
                          <linearGradient
                            id='paint0_linear_17829_51195'
                            x1='15.9518'
                            y1='8'
                            x2='0.0481925'
                            y2='8'
                            gradientUnits='userSpaceOnUse'
                          >
                            <stop stopColor='#7E0D9D' />
                            <stop offset='0.5' stopColor='#7328F2' />
                            <stop offset='1' stopColor='#51A0F7' />
                          </linearGradient>
                          <clipPath id='clip0_17829_51195'>
                            <rect width='16' height='16' fill='white' />
                          </clipPath>
                        </defs>
                      </svg>
                      Ask zAI
                    </span>
                    <span className='text-sm font-light flex gap-x-1 justify-center'>
                      Universal Result{' '}
                      <svg
                        width='20'
                        height='20'
                        viewBox='0 0 20 20'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'
                      >
                        <path
                          d='M5.83464 16.6663L1.66797 12.4997L5.83464 8.33301L7.0013 9.52051L4.85547 11.6663H10.8346V13.333H4.85547L7.0013 15.4788L5.83464 16.6663ZM14.168 11.6663L13.0013 10.4788L15.1471 8.33301H9.16797V6.66634H15.1471L13.0013 4.52051L14.168 3.33301L18.3346 7.49967L14.168 11.6663Z'
                          fill='white'
                        />
                      </svg>
                    </span>
                  </div>
                  <span className='flex items-center'>
                    <svg
                      width='24'
                      height='24'
                      viewBox='0 0 24 24'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                    >
                      <path
                        d='M12 21C9.7 21 7.69583 20.2375 5.9875 18.7125C4.27917 17.1875 3.3 15.2833 3.05 13H5.1C5.33333 14.7333 6.10417 16.1667 7.4125 17.3C8.72083 18.4333 10.25 19 12 19C13.95 19 15.6042 18.3208 16.9625 16.9625C18.3208 15.6042 19 13.95 19 12C19 10.05 18.3208 8.39583 16.9625 7.0375C15.6042 5.67917 13.95 5 12 5C10.85 5 9.775 5.26667 8.775 5.8C7.775 6.33333 6.93333 7.06667 6.25 8H9V10H3V4H5V6.35C5.85 5.28333 6.8875 4.45833 8.1125 3.875C9.3375 3.29167 10.6333 3 12 3C13.25 3 14.4208 3.2375 15.5125 3.7125C16.6042 4.1875 17.5542 4.82917 18.3625 5.6375C19.1708 6.44583 19.8125 7.39583 20.2875 8.4875C20.7625 9.57917 21 10.75 21 12C21 13.25 20.7625 14.4208 20.2875 15.5125C19.8125 16.6042 19.1708 17.5542 18.3625 18.3625C17.5542 19.1708 16.6042 19.8125 15.5125 20.2875C14.4208 20.7625 13.25 21 12 21ZM14.8 16.2L11 12.4V7H13V11.6L16.2 14.8L14.8 16.2Z'
                        fill='white'
                      />
                    </svg>
                  </span>
                </ShouldRender>
              </span>
            </span>
            <ShouldRender check={!messages.length}>
              <div
                className={clsx(
                  !isGlobal
                    ? 'AiChatBgImage bg-[#0A0A0A]'
                    : 'h-full w-full bg-grayZl-950',
                  ' overflow-hidden relative',
                )}
              >
                <ShouldRender check={isGlobal}>
                  <ShouldRender check={isGlobal}>
                    <div className='flex flex-col h-[60vh] items-center justify-center'>
                      <div className='flex justify-center items-center w-[80px] h-[80px] rounded-full bg-[linear-gradient(270deg,#7E0D9D_0.3%,#7328F2_50%,#51A0F7_99.7%)]'>
                        <motion.div
                          initial={{ scale: 1, rotate: 0 }}
                          animate={{
                            rotate: [0, 45, 45, 180, 176, 180],
                            scale: [1, 0.6, 0.6, 1.15, 0.95, 1],
                          }}
                          transition={{
                            duration: 2.0,
                            times: [0, 0.35, 0.55, 0.8, 0.9, 1],
                            ease: 'easeInOut',
                            repeat: Infinity,
                          }}
                        >
                          <StarWithoutBg width='32' />
                        </motion.div>
                      </div>
                      <div className='text-lg font-semibold mt-6 w-5/6 md:w-full text-center'>
                        Hello{userData?.name && `, `} {userData?.name + '!'}
                      </div>
                    </div>
                  </ShouldRender>
                </ShouldRender>
                <ShouldRender check={!isGlobal}>
                  <div className='flex flex-col h-full items-center  gap-y-1 '>
                    <div className=' flex justify-center w-full translate-y-1 mt-10'>
                      <Image
                        width={100}
                        height={300}
                        alt='BookImage'
                        src={`${baseUrl}/${Endpoints.getCoverImagesForBooks}/${(book_id ?? parsedBookId) as string}`}
                        className='z-50 rounded-sm'
                      />
                    </div>
                    <div className='text-sm mt-4 w-5/6 md:w-full text-center'>
                      {bookData?.name}
                    </div>
                    <div className='text-textDarkGrayColor text-xs text-center w-4/6 md:w-1/2'>
                      All answers generated by this AI come directly from the
                      book, without using any external information.
                    </div>
                  </div>
                </ShouldRender>

                <div
                  className={clsx(
                    isGlobal
                      ? 'bg-black border-t border-grayZl-900 py-2'
                      : 'px-5',
                    'absolute w-full h-min  bottom-1',
                  )}
                >
                  <div
                    className={clsx(
                      !isGlobal ? 'bg-[#272727]' : '',
                      'flex items-center  overflow-hidden shadow-lg rounded-full space-x-5 py-2  pl-3 md:pl-5 pr-2',
                    )}
                  >
                    <Input.TextArea
                      ref={textareaRef}
                      name='prompt'
                      value={prompt}
                      rows={1}
                      autoSize={{ minRows: 1, maxRows: 4 }}
                      onChange={(e) => setPrompt(e.target.value)}
                      className='bg-transparent w-full text-white placeholder:text-[#737373] font-normal text-sm md:text-base disabled:cursor-not-allowed disabled:text-gray-300 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-700 scrollbar-track-rounded-lg'
                      placeholder={
                        bookData?.name
                          ? screens.xl
                            ? `Ask Anything about ${bookData.name}`
                            : screens.xs
                              ? `Ask about ${bookData.name}`.substring(0, 18) +
                                '...'
                              : `Ask about ${bookData.name}`.substring(0, 25) +
                                '...'
                          : screens.xl
                            ? 'Ask Anything'
                            : screens.xs
                              ? 'Ask your question...'
                              : 'Ask something...'
                      }
                      onKeyDown={handleKeyDown}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        boxShadow: 'none',
                        outline: 'none',
                        resize: 'none',
                      }}
                    />

                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleAskQuestion();
                      }}
                      disabled={!prompt || chadProcessing}
                      className={clsx(
                        !isGlobal ? 'bg-black' : 'bg-grayZl-850',
                        ` ${
                          chadProcessing &&
                          'disabled:bg-[#11A37F] disabled:text-gray-300 disabled:cursor-not-allowed'
                        } text-white ${
                          prompt &&
                          'bg-[#0C6952] disabled:bg-gray-300 disabled:active:bg-gray-300 dark:disabled:bg-gray-900/10 dark:disabled:active:bg-gray-900/10 disabled:cursor-not-allowed disabled:hover:opacity-100'
                        } self-end font-bold w-10 h-10 p-2 flex justify-center items-center rounded-full`,
                      )}
                    >
                      <ShouldRender check={!isGlobal}>
                        <PaperAirplaneIconSolid className='h-4 w-4 rotate-90 ' />
                      </ShouldRender>
                      <ShouldRender check={isGlobal}>
                        <PaperAirplaneIcon className='h-4 w-4 rotate-90 text-grayZl-600' />
                      </ShouldRender>
                    </button>
                  </div>
                  <ShouldRender check={!isGlobal}>
                    <span className='text-[10px] text-center w-full py-2 flex justify-center text-[#737373]'>
                      While this AI is highly capable, inaccuracies may occur
                    </span>
                  </ShouldRender>
                </div>
              </div>
            </ShouldRender>

            <ShouldRender check={messages.length}>
              <div
                className={clsx(
                  !isGlobal
                    ? 'AiChatBgImage bg-[#0A0A0A]'
                    : 'h-full w-full bg-grayZl-950',
                  'flex flex-col overflow-hidden no-scrollbar',
                )}
                style={{ height: '100svh' }}
              >
                <span
                  ref={containerRef}
                  className={clsx(
                    !isGlobal
                      ? 'AiChatBgImage bg-[#0A0A0A]'
                      : 'h-full w-full bg-grayZl-950',
                    'z-10 gap-y-4 w-full cursor-default no-scrollbar relative overflow-y-auto flex flex-col ',
                  )}
                >
                  {/* <ShouldRender check={!loadingThread}> */}
                  <ScrollableFeed
                    className='px-4 pb-4 pt-1 no-scrollbar'
                    loading={chadProcessing}
                  >
                    <div className='flex gap-4 flex-col'>
                      {messages?.map((message, index) => {
                        const isLastMessage = index === messages.length;
                        const isLast = index === messages.length - 1;
                        return (
                          <AiChatMessage
                            key={`${message.sender.id}-${index}`}
                            ref={isLast ? lastMessageRef : null}
                            isSender={message.sender.id === 1}
                            showSenderName={true}
                            text={message.messages}
                            user={message?.sender?.name || undefined}
                            loading={isLastMessage && chadProcessing}
                            createdAt={message.createdAt}
                            bookName={bookData?.name}
                          />
                        );
                      })}
                      <ShouldRender check={chadProcessing}>
                        <div className='flex items-end gap-2 max-w-xs overflow-hidden md:mr-12'>
                          <Avatar
                            size={screens.md ? 40 : 30}
                            alt='profile-picture'
                            className='flex-shrink-0 select-none bg-[#393939] flex justify-center items-center'
                            draggable={false}
                            shape='circle'
                          >
                            <Logo />
                          </Avatar>
                          <div className='relative  max-w-full md:max-w-lg'>
                            {/* Ant Design Skeleton Button as background */}
                            <Skeleton.Button
                              active
                              size='large'
                              className='absolute top-0 left-0 w-full h-full'
                            />

                            {/* Custom Skeleton with word-by-word animation */}
                            <motion.div
                              role='status'
                              className='space-y-2 md:space-y-2.5 max-w-full md:max-w-lg relative p-4'
                              initial='hidden'
                              animate='visible'
                              variants={containerVariants}
                            >
                              {skeletonLines.map((lineWidths, index) => (
                                <motion.div
                                  key={index}
                                  className='flex items-center gap-x-2 w-full'
                                  style={{
                                    maxWidth: `${480 - index * 20}px`,
                                  }}
                                  variants={wordVariants}
                                >
                                  {lineWidths.map((width, i) => (
                                    <Skeleton.Button
                                      key={i}
                                      active
                                      className={`h-2.5  ${width}`}
                                    />
                                  ))}
                                </motion.div>
                              ))}
                            </motion.div>
                          </div>
                        </div>
                      </ShouldRender>
                    </div>
                  </ScrollableFeed>
                </span>
                <div
                  className={clsx(
                    isGlobal
                      ? 'bg-black border-t border-grayZl-900 py-2'
                      : 'px-5',
                    'w-full h-min',
                  )}
                >
                  <div
                    className={clsx(
                      !isGlobal ? 'bg-[#272727]' : '',
                      'flex items-center overflow-hidden shadow-lg rounded-full space-x-5 py-2 pl-3 md:pl-5 pr-2',
                    )}
                  >
                    <Input.TextArea
                      ref={textareaRef}
                      name='prompt'
                      value={prompt}
                      rows={1}
                      autoSize={{ minRows: 1, maxRows: 4 }}
                      onChange={(e) => setPrompt(e.target.value)}
                      className='bg-transparent w-full placeholder:text-[#737373] font-normal text-sm md:text-base disabled:cursor-not-allowed disabled:text-gray-300 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-700 scrollbar-track-rounded-lg'
                      placeholder={
                        isGlobal
                          ? 'Message Ask zAI'
                          : screens.xl
                            ? `Ask Anything about ${bookData?.name ?? ''}`
                            : screens.xs
                              ? `Ask about ${bookData?.name}`.substring(0, 18) +
                                '...'
                              : `Ask about ${bookData?.name}`.substring(0, 25) +
                                '...'
                      }
                      onKeyDown={handleKeyDown}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        boxShadow: 'none',
                        outline: 'none',
                        resize: 'none',
                      }}
                    />

                    <button
                      className={clsx(
                        !isGlobal ? 'bg-black' : 'bg-grayZl-850',
                        ` ${
                          chadProcessing &&
                          'disabled:bg-[#11A37F] disabled:cursor-not-allowed'
                        } text-white ${
                          prompt &&
                          'bg-[#0C6952]  disabled:bg-gray-300 disabled:active:bg-gray-300 dark:disabled:bg-gray-900/10 dark:disabled:active:bg-gray-900/10 disabled:cursor-not-allowed disabled:hover:opacity-100'
                        } self-end font-bold w-10 h-10 p-2 flex justify-center items-center rounded-full`,
                      )}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleAskQuestion();
                      }}
                      disabled={!prompt || chadProcessing}
                    >
                      <ShouldRender check={!isGlobal}>
                        <PaperAirplaneIconSolid className='h-4 w-4 rotate-90 ' />
                      </ShouldRender>
                      <ShouldRender check={isGlobal}>
                        <PaperAirplaneIcon className='h-4 w-4 rotate-90 text-grayZl-600' />
                      </ShouldRender>
                    </button>
                  </div>
                  <ShouldRender check={!isGlobal}>
                    <span className='text-[10px] text-center w-full py-2 flex justify-center text-[#737373]'>
                      While this AI is highly capable, inaccuracies may occur
                    </span>
                  </ShouldRender>
                </div>
              </div>
            </ShouldRender>
          </div>
        </div>
      </div>
    </div>
  );

  const drawerProps = {
    placement: 'right' as const,
    open: visible,
    width: screens.lg ? '50%' : screens.xl ? '55%' : '100%',
    height: '100%',
    closable: false,
    onClose: onCancel,
    mask: false,
    footer: false,
    style: {
      boxShadow: 'none',
      backdropFilter: 'none',
      transition: 'transform 0.3s ease, opacity 0.3s ease',
      zIndex: -100,
    },
    className: `lg:custom-drawer -z-10 w-full lg:bg-transparent h-full selectable transform bg-blur-none ${
      visible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
    }`,
  };
  return (
    <Drawer
      {...drawerProps}
      {...(!screens.lg && { getContainer: undefined })}
      {...(isFullScreen && { getContainer: false })}
    >
      <div
        ref={chatBoxRef}
        className='w-full h-full no-scrollbar overflow-hidden lg:parent-container'
      >
        {isFullScreen ? (
          renderContent
        ) : (
          <div
            className={clsx(
              !isGlobal
                ? !scrolled
                  ? 'lg:translate-y-32'
                  : 'lg:translate-y-16'
                : 'lg:translate-y-1',
              'w-full h-full  overflow-hidden transition-transform duration-300 ease-in-out',
            )}
          >
            {renderContent}
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default AiChatDrawer;
